import React, { useEffect, useRef, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { MessageCircle, Send, CheckCircle2, XCircle, Copy, Sparkles, RefreshCcw } from "lucide-react";
import { toast } from "sonner";

function StatusChip({ label, ok, testid }) {
  return (
    <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs ${ok ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-800"}`} data-testid={testid}>
      {ok ? <CheckCircle2 size={14}/> : <XCircle size={14}/>}
      <span className="font-medium">{label}</span>
    </div>
  );
}

export default function Chats() {
  const [sessions, setSessions] = useState([]);
  const [activeWaId, setActiveWaId] = useState(null);
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState("");
  const [status, setStatus] = useState(null);
  const [q, setQ] = useState("");
  const [loadingSend, setLoadingSend] = useState(false);
  const [voice, setVoice] = useState("alloy");
  const [voiceSend, setVoiceSend] = useState(false);
  const scrollRef = useRef(null);

  const loadSessions = async () => {
    const r = await api.get("/whatsapp/sessions", { params: q ? { q } : {} });
    setSessions(r.data);
  };
  const loadDetail = async (wa_id) => {
    const r = await api.get(`/whatsapp/sessions/${wa_id}`);
    setActive(r.data);
  };
  const loadStatus = async () => {
    const r = await api.get("/whatsapp/config-status");
    setStatus(r.data);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadSessions(); loadStatus(); }, [q]);
  useEffect(() => {
    if (activeWaId) loadDetail(activeWaId);
  }, [activeWaId]);
  useEffect(() => {
    if (active?.voice_preset) setVoice(active.voice_preset);
  }, [active?.voice_preset]);
  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const iv = setInterval(() => {
      loadSessions();
      if (activeWaId) loadDetail(activeWaId);
    }, 5000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeWaId]);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [active]);

  const send = async () => {
    if (!activeWaId || !reply.trim()) return;
    setLoadingSend(true);
    try {
      if (voiceSend) {
        // Update voice-preset for this conversation first (so future messages default to it)
        await api.patch(`/whatsapp/sessions/${activeWaId}/voice-preset`, { voice });
        const r = await api.post("/whatsapp/send-voice", { to: activeWaId, body: reply, voice });
        if (!r.data?.ok) throw new Error(r.data?.reason || "voice send failed");
        toast.success(`Voice note sent (${voice})`);
      } else {
        await api.post("/whatsapp/send", { to: activeWaId, body: reply });
        toast.success("Sent");
      }
      setReply("");
      await loadDetail(activeWaId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Send failed");
    } finally { setLoadingSend(false); }
  };

  const VOICES = [
    { v: "nova", label: "Nova (female, upbeat)" },
    { v: "shimmer", label: "Shimmer (female, bright)" },
    { v: "coral", label: "Coral (female, warm)" },
    { v: "fable", label: "Fable (neutral, expressive)" },
    { v: "alloy", label: "Alloy (neutral)" },
    { v: "sage", label: "Sage (neutral, measured)" },
    { v: "ash", label: "Ash (male, clear)" },
    { v: "echo", label: "Echo (male, calm)" },
    { v: "onyx", label: "Onyx (male, deep)" },
  ];

  const copy = (v) => { navigator.clipboard.writeText(v); toast.success("Copied"); };

  const webhookUrl = `${window.location.origin}/api/whatsapp/webhook`;

  const configReady = status && status.access_token_configured && status.phone_number_id_configured && status.verify_token_configured;

  return (
    <div className="p-8 space-y-6">
      <header>
        <div className="label-caps text-accent mb-2">Assistant</div>
        <h1 className="font-heading text-4xl tracking-tight text-primary flex items-center gap-3">
          WhatsApp bot <Sparkles size={24} className="text-accent"/>
        </h1>
        <p className="text-muted-foreground mt-2 max-w-2xl">
          AI-powered bot (Claude Sonnet 4.5) that talks to members on WhatsApp in English, Hindi, and Indian regional languages —
          collects enrollments, books Full Body Checkups, medicine orders, and lab tests — and pushes them straight into this CRM.
        </p>
      </header>

      {/* Setup panel */}
      <Card className="p-6 border-border bg-secondary/40" data-testid="whatsapp-setup-card">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="label-caps text-primary">Setup status</div>
          <Button size="sm" variant="ghost" onClick={loadStatus} className="rounded-full h-8">
            <RefreshCcw size={14} className="mr-1"/> Refresh
          </Button>
        </div>
        {status && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-3">
            <StatusChip label="Access Token" ok={status.access_token_configured} testid="chip-access-token"/>
            <StatusChip label="Phone Number ID" ok={status.phone_number_id_configured} testid="chip-phone-id"/>
            <StatusChip label="App Secret" ok={status.app_secret_configured} testid="chip-app-secret"/>
            <StatusChip label="Verify Token" ok={status.verify_token_configured} testid="chip-verify-token"/>
            <StatusChip label="LLM (Claude)" ok={status.llm_key_configured} testid="chip-llm"/>
          </div>
        )}
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="label-caps text-muted-foreground mb-1">Webhook Callback URL</div>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-xs bg-white border border-border rounded px-2 py-1.5 truncate" data-testid="webhook-url">{webhookUrl}</code>
              <Button size="icon" variant="outline" onClick={() => copy(webhookUrl)} className="h-8 w-8"><Copy size={14}/></Button>
            </div>
          </div>
          <div>
            <div className="label-caps text-muted-foreground mb-1">Verify Token</div>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-xs bg-white border border-border rounded px-2 py-1.5 truncate" data-testid="verify-token-value">{status?.verify_token_value || "—"}</code>
              <Button size="icon" variant="outline" onClick={() => copy(status?.verify_token_value || "")} className="h-8 w-8"><Copy size={14}/></Button>
            </div>
          </div>
        </div>
        {!configReady && (
          <div className="mt-4 text-sm text-amber-800 bg-amber-50 rounded-lg p-3">
            The bot infrastructure is deployed but at least one credential is missing. Add
            <code className="mx-1 px-1 bg-white border">WHATSAPP_ACCESS_TOKEN</code> and
            <code className="mx-1 px-1 bg-white border">WHATSAPP_APP_SECRET</code>
            to <code className="mx-1 px-1 bg-white border">/app/backend/.env</code> and restart the backend, then paste the webhook URL + verify token above into Meta &gt; App &gt; WhatsApp &gt; Configuration.
          </div>
        )}
      </Card>

      {/* Sessions + chat */}
      <div className="grid grid-cols-12 gap-4">
        <Card className="col-span-4 border-border overflow-hidden" data-testid="wa-session-list">
          <div className="p-4 border-b border-border flex items-center justify-between">
            <div className="label-caps text-primary">Conversations</div>
            <Badge variant="secondary" className="bg-secondary text-primary">{sessions.length}</Badge>
          </div>
          <div className="p-3 border-b border-border">
            <Input
              data-testid="wa-session-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by mobile number…"
              className="h-9 rounded-lg text-sm"
            />
          </div>
          <ScrollArea className="h-[460px]">
            {sessions.length === 0 && (
              <div className="p-8 text-center text-muted-foreground text-sm">
                <MessageCircle size={28} className="mx-auto mb-2 opacity-50"/>
                No conversations yet. Once a user messages your WhatsApp number they'll appear here.
              </div>
            )}
            {sessions.map((s) => (
              <button
                key={s.wa_id}
                data-testid={`wa-session-${s.wa_id}`}
                onClick={() => setActiveWaId(s.wa_id)}
                className={`w-full text-left px-4 py-3 border-b border-border/60 hover:bg-secondary transition-colors ${activeWaId === s.wa_id ? "bg-secondary" : ""}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium text-sm">+{s.wa_id}</span>
                  <span className="text-xs text-muted-foreground">{s.message_count} msg</span>
                </div>
                <div className="text-xs text-muted-foreground truncate mt-1">{s.last_message || "…"}</div>
                <div className="text-[10px] text-muted-foreground mt-1">{s.updated_at ? new Date(s.updated_at).toLocaleString() : ""}</div>
              </button>
            ))}
          </ScrollArea>
        </Card>

        <Card className="col-span-8 border-border overflow-hidden flex flex-col" data-testid="wa-chat-window">
          {!active ? (
            <div className="flex-1 flex items-center justify-center text-muted-foreground p-10">
              Select a conversation to view messages.
            </div>
          ) : (
            <>
              <div className="p-4 border-b border-border">
                <div className="font-medium">+{active.wa_id}</div>
                <div className="text-xs text-muted-foreground">
                  State: <span className="font-medium">{active.state || "idle"}</span>
                  {active.intent && <> · Intent: <span className="font-medium">{active.intent}</span></>}
                </div>
              </div>
              <div ref={scrollRef} className="flex-1 p-4 space-y-3 overflow-y-auto h-[440px] bg-background">
                {(active.history || []).map((m, i) => {
                  const mediaUrl = m.media_url ? `${API_BASE.replace(/\/api$/, "")}${m.media_url}` : null;
                  const isImage = m.media_type && m.media_type.startsWith("image/");
                  const msgKey = m.ts ? `${m.ts}-${m.direction}-${i}` : `msg-${i}`;
                  return (
                  <div key={msgKey} className={`flex ${m.direction === "outbound" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm shadow-sm ${
                      m.direction === "outbound"
                        ? "bg-primary text-primary-foreground rounded-br-sm"
                        : "bg-white border border-border rounded-bl-sm"
                    }`}>
                      {mediaUrl && isImage && (
                        <a href={mediaUrl} target="_blank" rel="noreferrer" data-testid={`wa-msg-image-${i}`}>
                          <img src={mediaUrl} alt="prescription" className="max-w-[240px] rounded-lg mb-1.5 border border-border" />
                        </a>
                      )}
                      {mediaUrl && !isImage && (m.media_type || "").startsWith("audio/") && (
                        <audio controls src={mediaUrl} data-testid={`wa-msg-audio-${i}`} className="max-w-[260px] mb-1.5" />
                      )}
                      {mediaUrl && !isImage && !(m.media_type || "").startsWith("audio/") && (
                        <a href={mediaUrl} target="_blank" rel="noreferrer" data-testid={`wa-msg-file-${i}`}
                          className="inline-flex items-center gap-1.5 underline text-primary">
                          📎 Open attachment
                        </a>
                      )}
                      <div className="whitespace-pre-wrap">{m.body}</div>
                      <div className={`text-[10px] mt-1 ${m.direction === "outbound" ? "text-white/60" : "text-muted-foreground"}`}>
                        {m.ts ? new Date(m.ts).toLocaleTimeString() : ""}{m.manual ? " · manual" : ""}
                      </div>
                    </div>
                  </div>
                );})}
              </div>
              <div className="p-3 border-t border-border flex flex-col gap-2">
                <div className="flex items-center gap-2 text-xs">
                  <label className="flex items-center gap-1.5 cursor-pointer" data-testid="voice-mode-toggle">
                    <input type="checkbox" checked={voiceSend} onChange={(e) => setVoiceSend(e.target.checked)} className="h-3.5 w-3.5"/>
                    <span className="font-medium">Send as voice note</span>
                  </label>
                  <select
                    data-testid="voice-preset-select"
                    disabled={!voiceSend}
                    value={voice}
                    onChange={async (e) => {
                      const v = e.target.value;
                      setVoice(v);
                      if (activeWaId) {
                        try {
                          await api.patch(`/whatsapp/sessions/${activeWaId}/voice-preset`, { voice: v });
                        } catch (err) {
                          console.warn("[Chats] voice-preset save failed:", err?.message || err);
                        }
                      }
                    }}
                    className="border border-border rounded-md px-2 py-1 text-xs bg-white disabled:opacity-50"
                  >
                    {VOICES.map((v) => <option key={v.v} value={v.v}>{v.label}</option>)}
                  </select>
                </div>
                <div className="flex gap-2">
                  <Input
                    data-testid="wa-reply-input"
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && send()}
                    placeholder={voiceSend ? "Type text — it will be spoken with the chosen voice…" : "Type a manual reply…"}
                    className="rounded-full"
                  />
                  <Button data-testid="wa-send-btn" onClick={send} disabled={loadingSend || !reply.trim()} className="bg-primary hover:bg-primary/90 rounded-full">
                    <Send size={14} className="mr-1"/> {voiceSend ? "Send voice" : "Send"}
                  </Button>
                </div>
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
